/** Render a byte count for the "Maximum file size" requirements line.
 * Values >= 1MB use whole MB (or one decimal for fractional caps); smaller
 * values fall back to whole KB. Intended for display only — the cap itself
 * is enforced numerically against `File.size`. */
export function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) {
    return `${mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1)}MB`;
  }
  const kb = bytes / 1024;
  return `${Math.round(kb)}KB`;
}

/** Render a MIME type as a short, screen-friendly label, e.g.
 * `image/jpeg` → "JPEG", `image/svg+xml` → "SVG+XML". If the input
 * doesn't contain a slash, the whole string is upper-cased. */
export function formatTypeLabel(mimeType: string): string {
  const suffix = mimeType.split('/')[1] ?? mimeType;
  return suffix.toUpperCase();
}

/** Compose the short "Supported formats: …" / "Maximum file size: …" lines
 * rendered below the drawer sub-description. Returns an empty array when
 * neither restriction is configured so callers can skip the wrapper. */
export function buildRequirementsLines(
  acceptedFileTypes?: string[],
  maxFileSizeBytes?: number,
): string[] {
  const lines: string[] = [];
  if (acceptedFileTypes && acceptedFileTypes.length > 0) {
    lines.push(`Supported formats: ${acceptedFileTypes.map(formatTypeLabel).join(', ')}`);
  }
  if (maxFileSizeBytes != null) {
    lines.push(`Maximum file size: ${formatFileSize(maxFileSizeBytes)}`);
  }
  return lines;
}
